import { type FieldValue } from 'firebase/firestore'
import {
  typedCollection,
  typedDoc,
  getCollection,
  getDocument,
  updateDocument,
  deleteDocument,
  addDoc,
  serverTimestamp,
  orderBy,
  where,
  query,
  onSnapshot,
  Timestamp,
} from '@/lib/firebase/firestore'
import type {
  EventDocument,
  ProductionDocument,
  TicketTypeDocument,
  TicketOrderDocument,
  TicketDocument,
  UserDocument,
} from '@/types'
import type { EventFormValues, TicketTypeFormValues } from '../types'
import { createUserFacingError } from '@/lib/errors'

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Convert "2024-12-25" + "19:30" → Timestamp */
function toTimestamp(dateStr: string, timeStr: string = '00:00'): Timestamp {
  return Timestamp.fromDate(new Date(dateStr + 'T' + timeStr + ':00'))
}

// ── Collection refs ────────────────────────────────────────────────────────────

const eventsCol = () => typedCollection<EventDocument>('events')
const productionDoc = (id: string) => typedDoc<ProductionDocument>('productions', id)
const ticketTypesCol = (eventId: string) =>
  typedCollection<TicketTypeDocument>('events/' + eventId + '/ticketTypes')
const ordersCol = () => typedCollection<TicketOrderDocument>('orders')
const ticketsCol = () => typedCollection<TicketDocument>('tickets')
const usersCol = () => typedCollection<UserDocument>('users')

// ── Payload types ──────────────────────────────────────────────────────────────

type EventCreatePayload = Omit<EventDocument, 'id' | 'createdAt' | 'updatedAt'> & {
  createdAt: FieldValue
  updatedAt: FieldValue
}

type TicketTypeCreatePayload = Omit<TicketTypeDocument, 'id' | 'createdAt' | 'updatedAt'> & {
  createdAt: FieldValue
  updatedAt: FieldValue
}

// ── Service ────────────────────────────────────────────────────────────────────

export const adminEventsService = {
  // ── Events ──────────────────────────────────────────────────────────────────

  async getAllEvents(): Promise<Array<EventDocument & { id: string }>> {
    return getCollection(eventsCol(), orderBy('order', 'asc'))
  },

  async getEventById(id: string): Promise<(EventDocument & { id: string }) | null> {
    const ref = typedDoc<EventDocument>('events', id)
    const doc = await getDocument(ref)
    if (!doc) return null
    return { ...doc, id }
  },

  async getEventBySlug(slug: string): Promise<(EventDocument & { id: string }) | null> {
    const results = await getCollection(eventsCol(), where('slug', '==', slug))
    return results[0] ?? null
  },

  async createEvent(data: EventFormValues, createdBy: string): Promise<string> {
    const existing = await this.getEventBySlug(data.slug)
    if (existing) {
      throw createUserFacingError(`An event with slug "${data.slug}" already exists.`)
    }

    if (data.productionId) {
      const production = await getDocument(productionDoc(data.productionId))
      if (!production) throw createUserFacingError('The linked production could not be found.')
    }

    const isProductionEvent = Boolean(data.productionId)
    const payload: EventCreatePayload = {
      title: data.title,
      slug: data.slug,
      description: data.description,
      venue: data.venue,
      eventDate: toTimestamp(data.eventDate, data.eventTime),
      doorTime: data.doorTime,
      status: data.status,
      isPublished: data.isPublished,
      publicSalesEnabled: !isProductionEvent,
      order: data.order,
      capacity: data.capacity,
      createdBy,
      ...(data.productionId
        ? { productionId: data.productionId, performanceOrder: data.order }
        : {}),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    const ref = await addDoc(eventsCol(), payload as unknown as EventDocument)
    return ref.id
  },

  async updateEvent(id: string, data: Partial<EventFormValues>): Promise<void> {
    const ref = typedDoc<EventDocument>('events', id)
    if (data.slug !== undefined) {
      const existing = await this.getEventBySlug(data.slug)
      if (existing && existing.id !== id) {
        throw createUserFacingError(`An event with slug "${data.slug}" already exists.`)
      }
    }
    const updates: Record<string, unknown> = { updatedAt: serverTimestamp() }
    if (data.title !== undefined) updates.title = data.title
    if (data.slug !== undefined) updates.slug = data.slug
    if (data.description !== undefined) updates.description = data.description
    if (data.venue !== undefined) updates.venue = data.venue
    if (data.eventDate !== undefined)
      updates.eventDate = toTimestamp(data.eventDate, data.eventTime ?? '00:00')
    if (data.doorTime !== undefined) updates.doorTime = data.doorTime
    if (data.status !== undefined) updates.status = data.status
    if (data.isPublished !== undefined) updates.isPublished = data.isPublished
    if (data.order !== undefined) updates.order = data.order
    if (data.capacity !== undefined) {
      const ticketTypes = await this.getTicketTypes(id)
      const committedSeats = ticketTypes.reduce(
        (total, ticketType) => total + ticketType.sold + (ticketType.reserved ?? 0),
        0,
      )
      if (data.capacity < committedSeats) {
        throw createUserFacingError(
          `Show capacity cannot be lower than the ${committedSeats} seats already sold or held.`,
        )
      }
      updates.capacity = data.capacity
    }
    await updateDocument(ref, updates as Partial<EventDocument>)
  },

  async deleteEvent(id: string): Promise<void> {
    await deleteDocument(typedDoc<EventDocument>('events', id))
  },

  // ── Ticket Types ────────────────────────────────────────────────────────────

  async getTicketTypes(eventId: string): Promise<Array<TicketTypeDocument & { id: string }>> {
    return getCollection(ticketTypesCol(eventId), orderBy('order', 'asc'))
  },

  async createTicketType(eventId: string, data: TicketTypeFormValues): Promise<string> {
    const payload: TicketTypeCreatePayload = {
      eventId,
      name: data.name,
      description: data.description,
      price: Math.round(data.price * 100), // dollars → cents
      capacity: data.capacity,
      sold: 0,
      reserved: 0,
      saleStartsAt: Timestamp.fromDate(new Date(data.saleStartsAt)),
      saleEndsAt: Timestamp.fromDate(new Date(data.saleEndsAt)),
      isVisible: data.isVisible,
      order: data.order,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    const ref = await addDoc(ticketTypesCol(eventId), payload as unknown as TicketTypeDocument)
    return ref.id
  },

  async updateTicketType(
    eventId: string,
    typeId: string,
    data: Partial<TicketTypeFormValues>,
  ): Promise<void> {
    const ref = typedDoc<TicketTypeDocument>('events/' + eventId + '/ticketTypes', typeId)
    const current = await getDocument(ref)
    if (!current) throw createUserFacingError('Ticket type not found.')
    const updates: Record<string, unknown> = { updatedAt: serverTimestamp() }
    if (data.name !== undefined) updates.name = data.name
    if (data.description !== undefined) updates.description = data.description
    if (data.price !== undefined) updates.price = Math.round(data.price * 100)
    if (data.capacity !== undefined) {
      const committedSeats = current.sold + (current.reserved ?? 0)
      if (data.capacity < committedSeats) {
        throw createUserFacingError(
          `Ticket type limit cannot be lower than the ${committedSeats} tickets already sold or held.`,
        )
      }
      updates.capacity = data.capacity
    }
    if (data.saleStartsAt !== undefined)
      updates.saleStartsAt = Timestamp.fromDate(new Date(data.saleStartsAt))
    if (data.saleEndsAt !== undefined)
      updates.saleEndsAt = Timestamp.fromDate(new Date(data.saleEndsAt))
    if (data.isVisible !== undefined) updates.isVisible = data.isVisible
    if (data.order !== undefined) updates.order = data.order
    await updateDocument(ref, updates as Partial<TicketTypeDocument>)
  },

  async deleteTicketType(eventId: string, typeId: string): Promise<void> {
    const ref = typedDoc<TicketTypeDocument>('events/' + eventId + '/ticketTypes', typeId)
    const current = await getDocument(ref)
    if (!current) return
    if (current.sold + (current.reserved ?? 0) > 0) {
      throw createUserFacingError('A ticket type with sold or held seats cannot be deleted. Hide it instead.')
    }
    await deleteDocument(ref)
  },

  // ── Orders & Tickets ────────────────────────────────────────────────────────

  async getOrdersForEvent(eventId: string): Promise<Array<TicketOrderDocument & { id: string }>> {
    return getCollection(ordersCol(), where('eventId', '==', eventId))
  },

  async getTicketsForEvent(eventId: string): Promise<Array<TicketDocument & { id: string }>> {
    return getCollection(ticketsCol(), where('eventId', '==', eventId))
  },

  // ── Users ───────────────────────────────────────────────────────────────────

  async getAllUsers(): Promise<Array<UserDocument & { id: string }>> {
    return getCollection(usersCol(), orderBy('email', 'asc'))
  },

  subscribeToTicketCounts(
    eventId: string,
    callback: (tickets: Array<TicketDocument & { id: string }>) => void,
  ): () => void {
    const q = query(ticketsCol(), where('eventId', '==', eventId))
    return onSnapshot(q, (snap) => {
      callback(snap.docs.map((d) => ({ ...d.data(), id: d.id })))
    })
  },
}
